import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfirmDialogProvider } from './contexts/ConfirmDialogContext';
import { GlobalNotificationUiProvider } from './contexts/GlobalNotificationUiContext';
import { GlobalContextProvider } from './contexts/GlobalContext';
import { ImagePreviewProvider } from './components/ImagePreviewDialog';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import IntrusionDashboard from './components/IntrusionDashboard';
import IntrusionProfileEnrollment from './components/IntrusionProfileEnrollment';
import Unavailable from './components/Unavailable';
import LogsPage from './components/LogsPage';
import ActivityLogs from './components/ActivityLogs';
import ActivityLogsPage from './components/ActivityLogsPage';
import AuditPresentView from './components/AuditPresentView';
import Intrusion from './components/Intrusion';
import UnmatchedGroups from './components/UnmatchedGroups';
import Employees from './components/Employees';
import EmployeeRegistration from './components/EmployeeRegistration';
import VendorRegistration from './components/VendorRegistration';
import Vendors from './components/Vendors';
import VisitorRegistration from './components/VisitorRegistration';
import Visitors from './components/Visitors';
import EmployeeDetails from './components/EmployeeDetails';
import VendorDetails from './components/VendorDetails';
import VisitorDetails from './components/VisitorDetails';
import UserManagement from './components/UserManagement';
import AccessControl from './components/AccessControl';
import CompanyConfiguration from './components/CompanyConfiguration';
import ApiKeys from './components/ApiKeys';
import ConnectWithKivo from './components/ConnectWithKivo';
import Settings from './components/Settings';
import NotificationSettings from './components/NotificationSettings';
import ActivitySettings from './components/ActivitySettings';
import WorkstationMonitoring from './components/WorkstationMonitoring';
import WorkspacesView from './components/WorkspacesView';
import VisionReview from './components/VisionReview';
import VisionReviewStatistics from './components/VisionReviewStatistics';
import Alerts from './components/Alerts';
import Layout from './components/Layout';
import Test from './components/Test';
import DbExplorer from './components/DbExplorer';
import { WebViewRouteGuard, CombinedWebViewRouteGuard } from './components/WebViewRouter';
import WebViewIntrusion from './components/WebViewIntrusion';
import CameraStreams from './components/CameraStreams';
import SpeakerConfiguration from './components/SpeakerConfiguration';
import HooterConfiguration from './components/HooterConfiguration';
import KivoCallback from './components/KivoCallback';
import ExportToKivo from './components/ExportToKivo';
import DailyReport from './components/DailyReport';
import IntrusionAccounts from './components/IntrusionAccounts';
import IntrusionProfiles from './components/IntrusionProfiles';
import CreateIntrusionProfile from './components/CreateIntrusionProfile';
import RecordingInterface from './components/RecordingInterface';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';
import WorkstationDashboard from "./components/workstations/workstationDashboard";
// Component to redirect authenticated users away from login page
const RedirectIfAuthenticated = ({ children }) => {
  const { isAuthenticated, permissions, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (isAuthenticated && permissions) {
    return <Navigate to={permissions.dashboard_path || '/dashboard'} replace />;
  }

  return children;
};

// Component to handle root route redirect
const RootRedirect = () => {
  const { isAuthenticated, permissions, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (permissions?.dashboard_path) {
    return <Navigate to={permissions.dashboard_path} replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

/** Preserve query string and location state when remapping legacy routes. */
const RedirectPreserveSearch = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace state={location.state} />;
};

// Create a Material-UI theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#40518A',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: 'Poppins, Arial, sans-serif',
    h4: {
      fontWeight: 600,
    },
  },
});

// App Routes Component (needs to be inside AuthProvider)
const AppRoutes = () => {
  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<RedirectIfAuthenticated><Login /></RedirectIfAuthenticated>} />
        <Route path="/kivo-callback" element={<KivoCallback />} />
        <Route path="/webview" element={<WebViewRouteGuard />} />
        <Route path="/webview/:laneSlug" element={<WebViewRouteGuard />} />
        <Route path="/webview-combined" element={<CombinedWebViewRouteGuard />} />

        {/* Intrusion user routes */}
        <Route
          path="/dashboard/intrusion-dashboard"
          element={<RedirectPreserveSearch to="/intrusion-dashboard" />}
        />
        <Route
          path="/intrusion-dashboard"
          element={
            <ProtectedRoute allowedRoles={['admin', 'intrusion']}>
              <Layout><IntrusionDashboard /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/webview-intrusion"
          element={
            <ProtectedRoute allowedRoles={['admin', 'intrusion']}>
              <WebViewIntrusion />
            </ProtectedRoute>
          }
        />
        <Route
          path="/webview-intrusion/:laneSlug"
          element={
            <ProtectedRoute allowedRoles={['admin', 'intrusion']}>
              <WebViewIntrusion />
            </ProtectedRoute>
          }
        />
        <Route
          path="/intrusion-logs"
          element={
            <ProtectedRoute allowedRoles={['intrusion']}>
              <Layout><Intrusion /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/intrusion-profile-enrollment"
          element={
            <ProtectedRoute allowedRoles={['intrusion']}>
              <Layout><IntrusionProfileEnrollment /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><Dashboard /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/:personId"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><EmployeeDetails /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/vendor/:personId"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><VendorDetails /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/visitor/:personId"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><VisitorDetails /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><Employees /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/register"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><EmployeeRegistration /></Layout>
            </ProtectedRoute>
          }
        />
        {/* Legacy People routes → top-level */}
        <Route path="/employees/vendors" element={<Navigate to="/vendors" replace />} />
        <Route path="/employees/vendors/register" element={<Navigate to="/vendors/register-vendor" replace />} />
        <Route path="/employees/vendors/register-vendor" element={<Navigate to="/vendors/register-vendor" replace />} />
        <Route path="/employees/visitors" element={<Navigate to="/visitors" replace />} />
        <Route path="/employees/visitors/register" element={<Navigate to="/visitors/register-visitor" replace />} />
        <Route path="/employees/visitors/register-visitor" element={<Navigate to="/visitors/register-visitor" replace />} />
        <Route
          path="/vendors/register-vendor"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><VendorRegistration /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/vendors"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><Vendors /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/visitors/register-visitor"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><VisitorRegistration /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/visitors"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><Visitors /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={<Navigate to="/user-management" replace />}
        />
        <Route
          path="/user-management"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><UserManagement /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/access-control"
          element={
            <ProtectedRoute allowedRoles={['admin']} requireSuperAdmin>
              <Layout><AccessControl /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-accesbilities"
          element={<Navigate to="/access-control" replace />}
        />
        <Route
          path="/company-configuration"
          element={
            <ProtectedRoute allowedRoles={['admin']} requireSuperAdmin>
              <Layout><CompanyConfiguration /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/api-keys"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><ApiKeys /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/connect-kivo"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><ConnectWithKivo /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/camera-streams"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><CameraStreams viewMode="camera-streams" /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/system-health-check"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><CameraStreams viewMode="system-health-check" /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/detection-settings"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><CameraStreams viewMode="detection-settings" /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/face-embedding-config"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><CameraStreams viewMode="face-embedding-config" /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/late-entry-config"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><CameraStreams viewMode="late-entry-config" /></Layout>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/recordings" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><RecordingInterface /></Layout>
            </ProtectedRoute>
          } 
        /> 
        <Route
          path="/banner-settings"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><Settings /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/speaker-configuration"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><SpeakerConfiguration /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hooter-configuration"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><HooterConfiguration /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notification-settings"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><NotificationSettings /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/activity-settings"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><ActivitySettings /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workstation-monitoring"
          element={
            <ProtectedRoute allowedRoles={['admin']} requireFeatureFlag="workstation_monitoring">
              <Layout><WorkstationMonitoring /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
  path="/workstation-dashboard"
  element={
    <ProtectedRoute allowedRoles={["admin"]}>
      <Layout>
        <WorkstationDashboard />
      </Layout>
    </ProtectedRoute>
  }
/>
        <Route
          path="/workstation-monitoring/workspaces/:cameraId"
          element={
            <ProtectedRoute allowedRoles={['admin']} requireFeatureFlag="workstation_monitoring">
              <Layout><WorkspacesView /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-review/statistics"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><VisionReviewStatistics /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-review"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><VisionReview /></Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/settings/ai-review" element={<Navigate to="/ai-review" replace />} />
        <Route
          path="/alerts"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><Alerts /></Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/settings/alerts" element={<Navigate to="/alerts" replace />} />
        <Route path="/settings/db-explorer" element={<Navigate to="/db-explorer" replace />} />
        <Route path="/debug/db-explorer" element={<Navigate to="/db-explorer" replace />} />
        <Route
          path="/db-explorer"
          element={
            <ProtectedRoute allowedRoles={[]}>
              <Layout><DbExplorer /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/test"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><Test /></Layout>
            </ProtectedRoute>
          }
        />
        {/* Legacy Attendance routes → top-level */}
        <Route path="/attendance" element={<RedirectPreserveSearch to="/present" />} />
        <Route path="/attendance/present" element={<RedirectPreserveSearch to="/present" />} />
        <Route path="/attendance/activity" element={<RedirectPreserveSearch to="/activity-tracking" />} />
        <Route path="/attendance/unavailable" element={<RedirectPreserveSearch to="/unavailable" />} />
        <Route path="/attendance/export-to-kivo" element={<RedirectPreserveSearch to="/export-to-kivo" />} />
        <Route path="/attendance/daily-report" element={<RedirectPreserveSearch to="/daily-report" />} />
        {/* Legacy Logs routes → top-level */}
        <Route path="/logs" element={<RedirectPreserveSearch to="/in-out" />} />
        <Route path="/logs/audit" element={<RedirectPreserveSearch to="/in-out" />} />
        <Route path="/logs/activity" element={<RedirectPreserveSearch to="/activity" />} />
        <Route path="/logs/audit/activity-logs" element={<RedirectPreserveSearch to="/activity" />} />
        <Route path="/audit" element={<RedirectPreserveSearch to="/in-out" />} />
        <Route
          path="/present"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><AuditPresentView /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/activity-tracking"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><ActivityLogs /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/unavailable"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><Unavailable /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/daily-report"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><DailyReport /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/export-to-kivo"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><ExportToKivo /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/in-out"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><LogsPage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/activity"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><ActivityLogsPage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/intrusion"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><Intrusion /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/unmatched-groups"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><UnmatchedGroups /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/accounts"
          element={<RedirectPreserveSearch to="/intrusion-accounts" />}
        />
        <Route
          path="/intrusion-accounts"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><IntrusionAccounts /></Layout>
            </ProtectedRoute>
          }
        />
        {/* Legacy Intrusion Profile routes → top-level */}
        <Route
          path="/employees/register-intrusion-profile"
          element={<Navigate to="/intrusion-profile/register-intrusion-profile" replace />}
        />
        <Route
          path="/employees/intrusion-profiles/register-intrusion-profile"
          element={<Navigate to="/intrusion-profile/register-intrusion-profile" replace />}
        />
        <Route
          path="/employees/intrusion-profiles"
          element={<Navigate to="/intrusion-profile" replace />}
        />
        <Route
          path="/intrusion-profile/register-intrusion-profile"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><CreateIntrusionProfile /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/intrusion-profile"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><IntrusionProfiles /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<RootRedirect />} />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ConfirmDialogProvider>
          <ImagePreviewProvider>
            <GlobalContextProvider>
              <GlobalNotificationUiProvider>
                  <AppRoutes />
              </GlobalNotificationUiProvider>
            </GlobalContextProvider>
          </ImagePreviewProvider>
        </ConfirmDialogProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
